export const en = {
  common: {
    loading: 'Loading...',
    unknown: 'Unknown',
    online: 'Online',
    offline: 'Offline',
    na: 'N/A',
    language: 'Language',
    minutesShort: '{{min}}m {{sec}}s',
  },
  app: {
    loadingAuth: 'Loading...',
  },
  login: {
    pageTitle: 'PostureXe Admin',
    pageSubtitle: 'Sign in to access the platform',
    username: 'Username',
    password: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in...',
    successToast: 'Signed in successfully',
    errorToast: 'Incorrect username or password',
  },
  logo: {
    tagline: 'Posture analysis',
  },
  layout: {
    systemStatus: 'System status',
    onlineDevices: 'Devices online',
    mainMenu: 'Main menu',
    logout: 'Sign out',
    openMenu: 'Open menu',
    mobileHeaderTitle: 'PostureXe Admin',
  },
  nav: {
    dashboard: 'Dashboard',
    sessions: 'Sessions',
    devices: 'Devices',
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Overview statistics and system monitoring',
    reportsLoadErrorTitle: 'Could not load reports',
    totalDevices: 'Total devices',
    onlineOnlineOffline: '{{online}} online, {{offline}} offline',
    onlineCardTitle: 'Online',
    onlineCardHint: 'Connected devices',
    offlineCardTitle: 'Offline',
    offlineCardHint: 'Not connected',
    qrStatsTitle: 'QR code access statistics',
    qrGenerated: 'QR codes generated',
    reportsAccessed: 'Reports accessed',
    accessRate: 'Access rate',
    trendsTitle: 'Posture analysis trends',
    timePeriod: 'Time period',
    lastDays: 'Last {{count}} days',
    chartSessions: 'Sessions',
    noTrendData: 'No trend data available',
    scoreDistributionTitle: 'Sessions by score category',
    noDataPeriod: 'No data for the selected period',
    sessionsByLocation: 'Sessions by location',
    barTooltipSessions: '{{count}} sessions',
    scoreCategory: {
      excellent: 'Excellent',
      good: 'Good',
      fair: 'Fair',
      poor: 'Poor',
      very_poor: 'Very poor',
    },
  },
  history: {
    title: 'Sessions',
    subtitle: 'View and manage posture analysis sessions',
    filters: 'Filters',
    allLocations: 'All locations',
    timePeriod: 'Time period',
    lastDays: 'Last {{count}} days',
    allSessions: 'All sessions',
    clearFilters: 'Clear filters',
    loadErrorTitle: 'Could not load sessions',
    loadingSessions: 'Loading sessions...',
    emptyFiltered: 'No sessions match the selected filters',
    empty: 'No sessions found',
    entrySingular: '{{count}} entry',
    entryPlural: '{{count}} entries',
    pageOf: 'Page {{current}} of {{total}}',
    status: {
      completed: 'Completed',
      in_progress: 'In progress',
      failed: 'Failed',
    },
    loadErrorGeneric: 'Failed to load session data',
  },
  reports: {
    title: 'Device list',
    subtitle: 'Monitor and manage NVIDIA Jetson devices',
    loadError: 'Failed to load devices. Please try again.',
    loading: 'Loading devices...',
    empty: 'No devices',
    statusLabel: 'Status:',
    uptimeLabel: 'Uptime:',
    otaEndpointLabel: 'OTA (public):',
  },
  deviceDetails: {
    loading: 'Loading device details...',
    loadErrorTitle: 'Could not load device details',
    backToDevices: 'Back to devices',
    deviceIdLabel: 'Device ID:',
    infoTitle: 'Device information',
    model: 'Model:',
    uptime: 'Uptime:',
    currentState: 'Current state:',
    lastHeartbeat: 'Last heartbeat:',
    lastSeen: 'Last seen:',
    locationTitle: 'Location',
    edit: 'Edit',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    name: 'Name:',
    address: 'Address:',
    locationUnsetTitle: 'Location not set',
    locationUnsetHint: 'Click "Edit" to configure the location',
    locationNameLabel: 'Location name',
    locationAddressLabel: 'Location address',
    locationNamePlaceholder: 'Enter location name',
    locationAddressPlaceholder: 'Enter location address',
    metricsTitle: 'Current metrics',
    cpuUsage: 'CPU usage:',
    memoryUsage: 'Memory usage:',
    performanceTitle: 'Performance metrics',
    range1h: '1h',
    range24h: '24h',
    range7d: '7d',
    cpuChartTitle: 'CPU usage',
    memoryChartTitle: 'Memory usage',
    last20Samples: 'Last 20 samples',
    noMetricsData: 'No metric data for the selected time range',
    updateLocationError: 'Could not update location',
    fetchError: 'Failed to fetch device details',
  },
  sessionDetails: {
    loading: 'Loading session details...',
    loadErrorTitle: 'Could not load session details',
    backToHistory: 'Back to history',
    title: 'Session details',
    sessionIdLabel: 'Session ID:',
    overallScore: 'Overall score',
    device: 'Device:',
    analyses: 'Analyses:',
    accuracy: 'Accuracy:',
    location: 'Location:',
    postureAnalysis: 'Posture analysis',
    viewFront: 'Front view',
    viewSide: 'Side view',
    viewBack: 'Rear view',
    shoulderAlignment: 'Shoulder alignment',
    hipAlignment: 'Hip alignment',
    neckAngle: 'Neck angle',
    spineCurvature: 'Spinal curvature',
    spineAlignment: 'Spinal alignment',
    shoulderBalance: 'Shoulder balance',
    legendOptimal: 'Optimal alignment',
    legendMinor: 'Minor deviation',
    legendNeedsCorrection: 'Needs correction',
    legendFootnote:
      'Colored areas indicate muscle groups and skeletal alignment relevant to posture assessment',
    metricOptimal: 'Optimal',
    metricModerate: 'Moderate',
    metricNeedsAttention: 'Needs attention',
    fetchFailed: 'Failed to fetch session details',
  },
  table: {
    score: 'Score',
    date: 'Date',
    status: 'Status',
    location: 'Location',
    device: 'Device',
  },
  errors: {
    requestTimeout: 'Request timeout',
    loadReportsData: 'Failed to load reports data',
  },
} as const;

export type TranslationSchema = typeof en;
